import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';

import { useAsyncData } from 'src/hooks/useAsyncData';
import { isLoading } from 'src/utils/request';
import { MerchantTitle } from 'app/MerchantTitle';
import { awardsApi } from 'components/awards/awardsApi';
import { IAwardsResponse } from 'components/awards/models';
import { AwardsList } from 'components/awards/ui/List';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { ELoyaltyProvider } from 'components/awards/enums';
import { AwardUserSection } from 'components/awards/ui/user-section/AwardUserSection';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { DialogLiveClock, dialogLiveClockSlotProps } from 'src/ui/DialogLiveClock';

export const AwardsPage: React.FC = () => {
    const { t } = useTranslation();
    const [awardsResponse, setAwardsResponse] = useAsyncData<IAwardsResponse>();
    const [showBarcodeDialog, setShowBarcodeDialog] = React.useState(false);
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    React.useEffect(() => {
        if (settings) {
            setAwardsResponse(() =>
                awardsApi.getAwards(
                    settings?.loyaltyProvider === ELoyaltyProvider.Square ||
                        settings?.loyaltyProvider === ELoyaltyProvider.Powercard
                )
            );
        }
    }, [setAwardsResponse, settings]);

    const { logUserEvent } = useGAHelpers();

    const barcodeIconEnabled =
        settings?.offlineCheckinEnabled && settings?.app?.awardsScreenBarcodePosition === 'TOOLBAR_BUTTON';

    const handleBarcodeIconClick = React.useCallback(() => {
        logUserEvent(EGAEventName.ShowBarcodeIconTap);
        setShowBarcodeDialog(true);
    }, [logUserEvent]);
    const handleBarcodeDialogClose = React.useCallback(() => {
        setShowBarcodeDialog(false);
    }, []);

    return (
        <Box>
            <MerchantTitle title={t('AWARDS_TITLE')} />
            <MuiAppBar title={t('AWARDS_TITLE')}>
                {barcodeIconEnabled && (
                    <IconButton onClick={handleBarcodeIconClick} size="large">
                        <QrCodeIcon color="primary" />
                    </IconButton>
                )}
            </MuiAppBar>
            <AwardsList isLoading={isLoading(awardsResponse)} awards={awardsResponse.data?.awards} />
            {barcodeIconEnabled && (
                <Dialog
                    fullWidth
                    open={showBarcodeDialog}
                    onClose={handleBarcodeDialogClose}
                    slotProps={dialogLiveClockSlotProps}
                >
                    <DialogLiveClock />
                    <Box padding={2}>
                        <AwardUserSection />
                    </Box>
                </Dialog>
            )}
        </Box>
    );
};
